/*
 * Recovery codes, shown exactly once.
 *
 * confirmSetup returns these on every successful TOTP enrolment — including an
 * update, where the set it returns *replaces* the one the user already has. The
 * service never returns them again, so this block cannot be dismissed by a
 * stray click on the mask or the close button: the caller gates its dialog on
 * `onDone`, which only fires once the user has ticked the acknowledgement.
 */
import { useState } from "react";
import { Checkbox } from "antd";
import { Check, Copy } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { btn, Callout } from "./mfaCommon";

import copyText from "../../../utils/copyText";
export default function BackupCodes({ codes = [], replaced, onDone }) {
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);

  const copyAll = async () => {
    try {
      await copyText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused (permissions, an insecure context).
      // The codes are on screen and selectable, so this is not a dead end —
      // say so rather than reporting a failure the user cannot act on.
      setCopied(false);
    }
  };

  return (
    <div style={{ fontFamily: AN_FONT }}>
      <Callout tone="warn">
        <strong style={{ fontWeight: 600 }}>Save these recovery codes now.</strong> Each one signs you
        in once if you lose your authenticator app. They are not shown again.
        {replaced && " Your previous codes no longer work."}
      </Callout>

      <div style={{
        display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8,
        margin: "14px 0", padding: 14,
        background: AN_COLORS.bg, border: `1px solid ${AN_COLORS.line}`, borderRadius: 8,
      }}>
        {codes.map((code) => (
          <span key={code} style={{
            fontFamily: AN_MONO, fontSize: 13, letterSpacing: 0.5,
            color: AN_COLORS.ink, textAlign: "center", userSelect: "all",
          }}>
            {code}
          </span>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <button type="button" onClick={copyAll} style={btn("secondary")}>
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy all"}
        </button>
        <Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)}>
          <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>I&rsquo;ve saved these codes</span>
        </Checkbox>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button type="button" onClick={onDone} disabled={!ack}
          style={{ ...btn("primary"), opacity: ack ? 1 : 0.5, cursor: ack ? "pointer" : "not-allowed" }}>
          Done
        </button>
      </div>
    </div>
  );
}
